import { useCallback, useState, useEffect } from 'react'
import {
  AgxColumn,
  AgxRadioGroup,
  AgxRow,
  AgxTextInput,
} from '@urbanx/agx.ui-components2'
import { DepositValueType } from '@urbanx/agx.campaigns.client'
import { parseNumber } from 'utils/parseNumber'

export enum NswDepositOption {
  TenPercent = '10%',
  FivePercent = '5%',
  CustomAmount = 'CustomAmount',
}

export enum NswDepositPayableOption {
  QuarterPercentUpfront = '0.25%',
  FullDepositUpfront = 'FullDepositUpfront',
}

export interface NswDepositSelection {
  depositOption: NswDepositOption
  depositPayableOption: NswDepositPayableOption
  percent?: number
  amount?: number
  depositValueType: DepositValueType
}

interface NswDepositOptionsProps {
  id: string
  onSelectionChange: (selection: NswDepositSelection) => void
}

const depositAmountOptions = [
  { value: NswDepositOption.TenPercent, label: '10%' },
  { value: NswDepositOption.FivePercent, label: '5%' },
  { value: NswDepositOption.CustomAmount, label: '$__' },
]

const depositPayableOptions = [
  {
    value: NswDepositPayableOption.QuarterPercentUpfront,
    label: '0.25% paid upfront',
  },
  {
    value: NswDepositPayableOption.FullDepositUpfront,
    label: 'Full deposit paid upfront',
  },
]

export function NswDepositOptionsComponent({
  id,
  onSelectionChange,
}: NswDepositOptionsProps) {
  const [depositOption, setDepositOption] = useState<NswDepositOption>(
    NswDepositOption.TenPercent
  )
  const [depositPayableOption, setDepositPayableOption] =
    useState<NswDepositPayableOption>(
      NswDepositPayableOption.QuarterPercentUpfront
    )
  const [customAmount, setCustomAmount] = useState<number | undefined>(
    undefined
  )

  useEffect(() => {
    let percent: number | undefined
    let amount: number | undefined
    let depositValueType: DepositValueType

    if (depositOption === NswDepositOption.TenPercent) {
      percent = 10
      depositValueType = DepositValueType.Percentage
    } else if (depositOption === NswDepositOption.FivePercent) {
      percent = 5
      depositValueType = DepositValueType.Percentage
    } else {
      amount = customAmount
      depositValueType = DepositValueType.Dollar
    }

    onSelectionChange({
      depositOption,
      depositPayableOption,
      percent,
      amount,
      depositValueType,
    })
  }, [depositOption, depositPayableOption, customAmount])

  const handleDepositOptionChange = useCallback((value: string) => {
    setDepositOption(value as NswDepositOption)
  }, [])

  const handleDepositPayableOptionChange = useCallback((value: string) => {
    setDepositPayableOption(value as NswDepositPayableOption)
  }, [])

  const handleCustomAmountChange = useCallback((value: string | undefined) => {
    const amount = value ? parseNumber(value) : undefined
    setCustomAmount(amount)
  }, [])

  return (
    <AgxColumn gap="spacing-lg" fill>
      <AgxColumn gap="spacing-md" fill>
        <AgxRadioGroup
          id={`${id}-deposit-option`}
          label="Deposit"
          layout="stacked"
          options={depositAmountOptions}
          value={depositOption}
          onChange={handleDepositOptionChange}
        />
        {depositOption === NswDepositOption.CustomAmount && (
          <AgxRow gap="spacing-md" fill>
            <AgxTextInput
              id={`${id}-custom-amount`}
              type="currency"
              stretch
              hideOptionalText
              label="Custom Amount"
              defaultValue={customAmount?.toString()}
              onChange={handleCustomAmountChange}
            />
          </AgxRow>
        )}
      </AgxColumn>

      <AgxRadioGroup
        id={`${id}-deposit-payable-option`}
        label="Deposit Payable"
        layout="stacked"
        options={depositPayableOptions}
        value={depositPayableOption}
        onChange={handleDepositPayableOptionChange}
      />
    </AgxColumn>
  )
}

/**
 * NSW Deposit Calculation Helper Functions
 *
 * When '0.25% paid upfront' is used:
 * - Deposit amounts are calculated from the Sale Price
 * - Initial Deposit = 0.25% of the Sale Price
 * - Balance Deposit = Total Deposit - Initial Deposit
 *
 * When 'Full Deposit Paid upfront' is used:
 * - Initial Deposit = Total Deposit (calculated from % or $ amount selected)
 * - No Balance deposit
 */
export function calculateNswDeposits(
  salePrice: number,
  depositOption: NswDepositOption,
  depositPayableOption: NswDepositPayableOption,
  customAmount?: number
): { initialDeposit: number; balanceDeposit: number | undefined } {
  let totalDeposit: number

  switch (depositOption) {
    case NswDepositOption.TenPercent:
      totalDeposit = salePrice * 0.1
      break
    case NswDepositOption.FivePercent:
      totalDeposit = salePrice * 0.05
      break
    case NswDepositOption.CustomAmount:
      totalDeposit = customAmount || 0
      break
    default:
      totalDeposit = 0
  }

  if (depositPayableOption === NswDepositPayableOption.FullDepositUpfront) {
    return {
      initialDeposit: totalDeposit,
      balanceDeposit: undefined,
    }
  } else {
    // 0.25% paid upfront
    const initialDeposit = salePrice * 0.0025
    const balanceDeposit = totalDeposit - initialDeposit
    return {
      initialDeposit,
      balanceDeposit: balanceDeposit > 0 ? balanceDeposit : undefined,
    }
  }
}
